import React from 'react';
import {
  Modal as RNModal,
  Platform,
  Pressable,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import {KeyboardAwareScrollView} from 'react-native-keyboard-aware-scroll-view';
import RNMModal, {SupportedAnimation} from 'react-native-modal';

import {useVisualViewportHeight} from '../../hooks/useVisualViewportHeight';

interface CrossPlatformModalProps {
  // Common modal props
  isVisible: boolean;
  children: React.ReactNode;

  // Backdrop props
  hasBackdrop?: boolean;
  backdropColor?: string;
  backdropOpacity?: number;
  onBackdropPress?: () => void;

  // Animation props
  animationType?: 'none' | 'slide' | 'fade';
  animationIn?: SupportedAnimation;
  animationOut?: SupportedAnimation;
  animationInTiming?: number;
  animationOutTiming?: number;

  // Android-specific props
  onBackButtonPress?: () => void;

  // Style props
  style?: StyleProp<ViewStyle>;

  // Modal behavior props
  avoidKeyboard?: boolean;
  /**
   * Android-only. Wraps children in a KeyboardAwareScrollView so content can scroll
   * clear of the keyboard, since the app runs `adjustPan` app-wide and the window
   * never resizes on Android.
   *
   * Has no effect on iOS — react-native-modal already applies KeyboardAvoidingView
   * `behavior="padding"` there, and stacking a second keyboard-avoidance layer on top
   * double-compensates and shoves the modal off-screen — or on web, where the web
   * branch handles the keyboard via `visualViewport` instead.
   *
   * Do NOT enable this on modals whose children already contain a scroll container:
   * SuburbSelectionModal renders a FlatList as a direct child (would nest a
   * VirtualizedList inside a ScrollView), and CardTokeniserTillModal already has its
   * own KeyboardAwareScrollView.
   */
  keyboardAwareContent?: boolean;
  onModalShow?: () => void;
  onModalHide?: () => void;

  // Web-specific override props
  webContainerStyle?: StyleProp<ViewStyle>;
}

const CrossPlatformModal: React.FC<CrossPlatformModalProps> = ({
  isVisible,
  children,
  hasBackdrop = true,
  backdropColor = 'black',
  backdropOpacity = 0.95,
  onBackdropPress,
  animationType = 'fade',
  animationIn = 'fadeIn',
  animationOut = 'fadeOut',
  animationInTiming = 300,
  animationOutTiming = 300,
  onBackButtonPress,
  style,
  avoidKeyboard,
  keyboardAwareContent,
  onModalShow,
  onModalHide,
  webContainerStyle,
  ...restProps
}) => {
  // On mobile web, use visual viewport height to position content above the keyboard.
  // When the keyboard opens, visualViewport.height shrinks while innerHeight stays constant.
  // offsetTop tracks how far iOS Safari has scrolled the page up for the keyboard.
  // Setting explicit height + top on the backdrop constrains the centering area so the modal
  // content stays visible and centered above the keyboard.
  const {height: visualViewportHeight, offsetTop: visualViewportOffsetTop} =
    useVisualViewportHeight();
  const keyboardAvoidStyle =
    Platform?.OS === 'web' && avoidKeyboard && visualViewportHeight > 0
      ? ({
          height: visualViewportHeight,
          top: visualViewportOffsetTop,
          bottom: 'auto',
        } satisfies ViewStyle)
      : undefined;

  // Web implementation using native React Native Modal
  if (Platform?.OS === 'web') {
    return (
      <RNModal
        visible={isVisible}
        transparent={true}
        animationType={animationType}
        onRequestClose={onBackdropPress}
        onShow={onModalShow}
        onDismiss={onModalHide}
        {...restProps}>
        {hasBackdrop ? (
          <Pressable
            style={[
              styles.webModalBackdrop,
              getWebBackdropStyle(),
              {
                backgroundColor:
                  backdropColor === 'black'
                    ? `rgba(0, 0, 0, ${backdropOpacity})`
                    : backdropColor === 'white'
                      ? `rgba(255, 255, 255, ${backdropOpacity})`
                      : backdropColor.startsWith('#')
                        ? `${backdropColor}${Math.round(backdropOpacity * 255)
                            .toString(16)
                            .padStart(2, '0')
                            .toUpperCase()}`
                        : `rgba(0, 0, 0, ${backdropOpacity})`, // fallback to black
              },
              style, // Apply the style prop for proper positioning
              webContainerStyle,
              keyboardAvoidStyle,
            ]}
            onPress={onBackdropPress}>
            <Pressable style={styles.webModalContentWrapper} onPress={e => e.stopPropagation()}>
              {children}
            </Pressable>
          </Pressable>
        ) : (
          <View style={[styles.webModalContent, style, webContainerStyle]}>{children}</View>
        )}
      </RNModal>
    );
  }

  // Mobile implementation using react-native-modal
  const nativeContent =
    Platform?.OS === 'android' && keyboardAwareContent ? (
      <KeyboardAwareScrollView
        enableOnAndroid
        extraScrollHeight={24}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.keyboardAwareContent}>
        {children}
      </KeyboardAwareScrollView>
    ) : (
      children
    );

  return (
    <RNMModal
      isVisible={isVisible}
      hasBackdrop={hasBackdrop}
      backdropColor={backdropColor}
      backdropOpacity={backdropOpacity}
      onBackdropPress={onBackdropPress}
      animationIn={animationIn}
      animationOut={animationOut}
      animationInTiming={animationInTiming}
      animationOutTiming={animationOutTiming}
      {...(Platform?.OS === 'android' && onBackButtonPress && {onBackButtonPress})}
      style={style}
      avoidKeyboard={avoidKeyboard}
      onModalShow={onModalShow}
      onModalHide={onModalHide}
      useNativeDriver={true}
      useNativeDriverForBackdrop={true}
      hideModalContentWhileAnimating={true}
      statusBarTranslucent={true}
      {...restProps}>
      {nativeContent}
    </RNMModal>
  );
};

const baseBackdropStyle = {
  position: 'absolute' as const,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  justifyContent: 'center' as const,
  alignItems: 'center' as const,
};

const getWebBackdropStyle = () => {
  // Safely check Platform.OS to avoid test environment issues
  const isWeb = Platform?.OS === 'web';

  return isWeb
    ? {
        ...baseBackdropStyle,
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
      }
    : baseBackdropStyle;
};

const styles = StyleSheet.create({
  webModalBackdrop: baseBackdropStyle,
  webModalContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  webModalContentWrapper: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  keyboardAwareContent: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default CrossPlatformModal;
